import type {
  StorageService,
  Batch,
  StorageUsage,
  CleanupResult,
  ItemMetadata,
  ValidationResult,
} from '../contracts/storageService'
import { StorageQuotaError } from '../contracts/storageService'

export class LocalStorageService implements StorageService {
  private readonly storageKey = 'image-tagger-batches'
  private readonly metadataKey = 'image-tagger-metadata'
  private changeListeners: Array<() => void> = []

  async saveBatch(batch: Batch): Promise<void> {
    try {
      const batches = await this.loadAllBatches()
      const batchToSave = {
        ...batch,
        updatedAt: new Date(),
      }
      
      // Calculate the size of the new batch data
      const batchSize = this.calculateBatchSize(batchToSave)
      const currentUsage = await this.getStorageUsage()
      
      // Check if we have enough space (leaving 10% buffer)
      const requiredSpace = batchSize + (currentUsage.total * 0.1) // 10% buffer
      if (currentUsage.used + batchSize > currentUsage.total - requiredSpace) {
        throw new StorageQuotaError(
          `Not enough storage space. Required: ${Math.round(batchSize / 1024)}KB, Available: ${Math.round(currentUsage.available / 1024)}KB`,
          currentUsage.used + batchSize,
          currentUsage.total
        )
      }

      batches[batch.id] = batchToSave

      try {
        localStorage.setItem(this.storageKey, JSON.stringify(batches))
        this.updateMetadata(batchToSave)
        this.notifyChange()
      } catch (error) {
        if (error instanceof DOMException && error.name === 'QuotaExceededError') {
          throw new StorageQuotaError(
            'Browser storage quota exceeded. Please clean up old batches or export your data.',
            currentUsage.used + batchSize,
            currentUsage.total
          )
        }
        throw error
      }
    } catch (error) {
      if (error instanceof StorageQuotaError) {
        throw error
      }
      throw new Error(`Failed to save batch: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async loadBatch(id: string): Promise<Batch | null> {
    try {
      const batches = await this.loadAllBatches()
      const batch = batches[id]
      return batch ? this.deserializeBatch(batch) : null
    } catch (error) {
      throw new Error(`Failed to load batch: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async listBatches(): Promise<Batch[]> {
    try {
      const batches = await this.loadAllBatches()
      return Object.values(batches).map(batch => this.deserializeBatch(batch))
    } catch (error) {
      throw new Error(`Failed to list batches: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async deleteBatch(id: string): Promise<void> {
    try {
      const batches = await this.loadAllBatches()
      delete batches[id]
      localStorage.setItem(this.storageKey, JSON.stringify(batches))
      this.removeFromMetadata(id)
      this.notifyChange()
    } catch (error) {
      throw new Error(`Failed to delete batch: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async getStorageUsage(): Promise<StorageUsage> {
    try {
      const batches = await this.loadAllBatches()
      const batchEntries = Object.values(batches)

      // Calculate storage usage
      const totalSize = batchEntries.reduce((sum, batch) => {
        return sum + this.calculateBatchSize(batch)
      }, 0)

      // Estimate available storage (localStorage has ~5-10MB limit)
      const estimatedLimit = 5 * 1024 * 1024 // 5MB conservative estimate
      const available = Math.max(0, estimatedLimit - totalSize)

      return {
        used: totalSize,
        available,
        total: estimatedLimit,
        percentage: (totalSize / estimatedLimit) * 100,
        itemCount: batchEntries.length,
      }
    } catch (error) {
      throw new Error(`Failed to get storage usage: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async hasSpace(requiredBytes: number): Promise<boolean> {
    try {
      const usage = await this.getStorageUsage()
      return usage.available >= requiredBytes
    } catch (error) {
      return false // Assume no space if we can't check
    }
  }

  async cleanup(): Promise<CleanupResult> {
    try {
      const startTime = Date.now()
      const batches = await this.loadAllBatches()
      const batchEntries = Object.entries(batches)

      // Find old batches (older than 30 days)
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      const oldBatches = batchEntries.filter(([, batch]) => {
        const batchDate = new Date(batch.createdAt)
        return batchDate < thirtyDaysAgo
      })

      let itemsDeleted = 0
      let bytesFreed = 0
      const errors: string[] = []

      // Delete old batches
      for (const [id, batch] of oldBatches) {
        try {
          const batchSize = this.calculateBatchSize(batch)
          delete batches[id]
          itemsDeleted++
          bytesFreed += batchSize
        } catch (error) {
          errors.push(`Failed to delete batch ${id}: ${error instanceof Error ? error.message : 'Unknown error'}`)
        }
      }

      // Save updated batches if any were deleted
      if (itemsDeleted > 0) {
        localStorage.setItem(this.storageKey, JSON.stringify(batches))
        this.notifyChange()
      }

      const duration = Date.now() - startTime

      return {
        itemsDeleted,
        bytesFreed,
        duration,
        errors,
      }
    } catch (error) {
      throw new Error(`Cleanup failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async exportData(): Promise<string> {
    try {
      const batches = await this.loadAllBatches()
      const metadata = this.getStorageMetadata()

      const exportData = {
        version: '1.0',
        exportedAt: new Date().toISOString(),
        batches,
        metadata,
      }

      return JSON.stringify(exportData, null, 2)
    } catch (error) {
      throw new Error(`Failed to export data: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async importData(data: string): Promise<void> {
    try {
      const importData = JSON.parse(data)

      if (!importData.batches) {
        throw new Error('Invalid import data: missing batches')
      }

      // Validate the structure
      for (const [id, batch] of Object.entries(importData.batches)) {
        if (!this.isValidBatch(batch)) {
          throw new Error(`Invalid batch data for ID: ${id}`)
        }
      }

      // Import the data
      localStorage.setItem(this.storageKey, JSON.stringify(importData.batches))

      if (importData.metadata) {
        localStorage.setItem(this.metadataKey, JSON.stringify(importData.metadata))
      }

      this.notifyChange()
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new Error('Invalid JSON data provided for import')
      }
      throw new Error(`Failed to import data: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  onStorageChange(callback: () => void): () => void {
    this.changeListeners.push(callback)
    return () => {
      const index = this.changeListeners.indexOf(callback)
      if (index > -1) {
        this.changeListeners.splice(index, 1)
      }
    }
  }

  async validateStorage(): Promise<ValidationResult> {
    const errors: string[] = []
    const warnings: string[] = []

    try {
      // Check if localStorage is available
      if (typeof localStorage === 'undefined') {
        errors.push('localStorage is not available')
        return { isValid: false, errors, warnings }
      }

      // Test localStorage functionality
      const testKey = 'image-tagger-test'
      const testValue = 'test'

      try {
        localStorage.setItem(testKey, testValue)
        const retrieved = localStorage.getItem(testKey)
        localStorage.removeItem(testKey)

        if (retrieved !== testValue) {
          errors.push('localStorage read/write test failed')
        }
      } catch (error) {
        errors.push(`localStorage test failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
      }

      // Check storage usage
      const usage = await this.getStorageUsage()
      if (usage.percentage > 90) {
        warnings.push(`Storage usage is ${usage.percentage.toFixed(1)}% - consider cleanup`)
      }

      // Validate existing data structure
      const batches = await this.loadAllBatches()
      for (const [id, batch] of Object.entries(batches)) {
        if (!this.isValidBatch(batch)) {
          errors.push(`Invalid batch structure for ID: ${id}`)
        }
      }

    } catch (error) {
      errors.push(`Storage validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    }
  }

  async getItemMetadata(key: string): Promise<ItemMetadata | null> {
    try {
      const batch = await this.loadBatch(key)
      if (!batch) return null

      return {
        key,
        size: this.calculateBatchSize(batch),
        created: new Date(batch.createdAt),
        accessed: new Date(), // Current time as access time
        type: 'batch',
      }
    } catch (error) {
      return null
    }
  }

  private async loadAllBatches(): Promise<Record<string, any>> {
    try {
      const stored = localStorage.getItem(this.storageKey)
      return stored ? JSON.parse(stored) : {}
    } catch (error) {
      console.warn('Failed to load batches from storage, returning empty object', error)
      return {}
    }
  }

  private deserializeBatch(batch: any): Batch {
    // Convert date strings back to Date objects
    return {
      ...batch,
      createdAt: new Date(batch.createdAt),
      updatedAt: batch.updatedAt ? new Date(batch.updatedAt) : undefined,
      context: batch.context ? {
        ...batch.context,
        date: batch.context.date ? new Date(batch.context.date) : undefined,
      } : undefined,
      images: batch.images?.map((image: any) => ({
        ...image,
        metadata: image.metadata ? {
          ...image.metadata,
          generatedAt: new Date(image.metadata.generatedAt),
          editedAt: image.metadata.editedAt ? new Date(image.metadata.editedAt) : undefined,
        } : undefined,
      })) || [],
    }
  }

  private calculateBatchSize(batch: any): number {
    // Estimate size based on JSON string length
    try {
      return JSON.stringify(batch).length * 2 // Rough estimate including overhead
    } catch (error) {
      return 1000 // Default size estimate
    }
  }

  private updateMetadata(batch: Batch): void {
    try {
      const metadata = this.getStorageMetadata()
      metadata[batch.id] = {
        lastModified: new Date().toISOString(),
        imageCount: batch.images?.length || 0,
        size: this.calculateBatchSize(batch),
      }
      localStorage.setItem(this.metadataKey, JSON.stringify(metadata))
    } catch (error) {
      console.warn('Failed to update metadata', error)
    }
  }

  private removeFromMetadata(batchId: string): void {
    try {
      const metadata = this.getStorageMetadata()
      delete metadata[batchId]
      localStorage.setItem(this.metadataKey, JSON.stringify(metadata))
    } catch (error) {
      console.warn('Failed to remove from metadata', error)
    }
  }

  private getStorageMetadata(): Record<string, any> {
    try {
      const stored = localStorage.getItem(this.metadataKey)
      return stored ? JSON.parse(stored) : {}
    } catch (error) {
      return {}
    }
  }

  private isValidBatch(batch: any): boolean {
    return (
      batch &&
      typeof batch === 'object' &&
      typeof batch.id === 'string' &&
      batch.createdAt &&
      Array.isArray(batch.images)
    )
  }

  private notifyChange(): void {
    this.changeListeners.forEach(listener => {
      try {
        listener()
      } catch (error) {
        console.warn('Storage change listener error:', error)
      }
    })
  }
}
